import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tagApis } from '../../../gateways/breadstack/tagApis'
import type { Tag, TagFormValues, TagListParams } from '../../../types/tag'

export const tagListQueryKey = (
  storeId: string,
  params: Partial<TagListParams>,
) => ['tags', storeId, params] as const

export const tagDetailQueryKey = (
  storeId: string,
  tagId: number | null | undefined,
) => ['tag-detail', storeId, tagId] as const

export const useTagPaginate = (
  storeId: string,
  params: Partial<TagListParams>,
) =>
  useQuery({
    queryKey: tagListQueryKey(storeId, params),
    queryFn: () => tagApis.paginate(storeId, params),
    enabled: Boolean(storeId),
    placeholderData: (prev) => prev,
  })

// Detail fetch hook for the drawer. Returns the full tag including
// `slugHistory[]` for the active-301-redirect chip section. Only enabled when
// `tagId` is truthy (i.e. edit mode); create mode never fires this query.
export const useTagDetail = (
  storeId: string,
  tagId: number | null | undefined,
) =>
  useQuery({
    queryKey: tagDetailQueryKey(storeId, tagId),
    queryFn: () => tagApis.getDetail(storeId, tagId as number),
    enabled: Boolean(storeId && tagId),
  })

export const useCreateTag = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (values: TagFormValues) => tagApis.create(storeId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags', storeId] })
    },
  })
}

export const useUpdateTag = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (values: TagFormValues) => tagApis.update(storeId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags', storeId] })
    },
  })
}

export const useUpdateTagVisibility = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ tag, visible }: { tag: Tag; visible: boolean }) =>
      tagApis.updateVisibility(storeId, tag, visible),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags', storeId] })
    },
  })
}

export const useDeleteTag = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => tagApis.deleteOne(storeId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags', storeId] })
    },
  })
}

export const useDeleteTagsBulk = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ids: number[]) => tagApis.deleteBulk(storeId, ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags', storeId] })
    },
  })
}

export const useBulkUpdateTagVisibility = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      tags,
      visible,
    }: { tags: Tag[]; visible: boolean }) => {
      await Promise.all(
        tags.map((tag) => tagApis.updateVisibility(storeId, tag, visible)),
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags', storeId] })
    },
  })
}

// Mutation that removes a single active 301 redirect entry from the tag's
// slug history. On success invalidates BOTH the tag-detail query (so the
// drawer chip list refreshes) and the tag list (so the `redirect_count`
// column / badge can update too).
export const useDeleteTagSlugHistory = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      tagId,
      slugHistoryId,
    }: { tagId: number; slugHistoryId: string }) =>
      tagApis.deleteSlugHistory(storeId, tagId, slugHistoryId),
    onSuccess: (_data, { tagId }) => {
      queryClient.invalidateQueries({
        queryKey: tagDetailQueryKey(storeId, tagId),
      })
      queryClient.invalidateQueries({ queryKey: ['tags', storeId] })
    },
  })
}
